/**
 * What smart tagging has DONE — `GET /api/okf/enrichment/activity`
 * (`src/enrichment/routes.ts`, FRONT-2550 round 3): the newest items Eddy
 * tagged, and — when a `since` is passed — how many passes a run has finished
 * since it started (counted from the tried-at stamp, tagged or not). Both come from the database, never from the in-process
 * queue, so any replica answers the same.
 *
 * The cache key carries BOTH inputs that change the request (`since`, `limit`)
 * — a poll for a new run's window must never be served the previous run's
 * numbers (CLAUDE.md, FRONT-1501). `keepPreviousData` keeps the "Recently
 * tagged" list on screen while a new window's first answer is in flight — so
 * a caller reading `sinceCounts` MUST ignore them while `isPlaceholderData`
 * is true (they describe the previous window).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { knowledgeEnrichmentKeys } from './use-knowledge-enrichment';

export interface RecentlyTaggedItem {
  source: 'file' | 'document';
  /** An upload's id, or a document's filename. */
  ref: string;
  name: string;
  isImage: boolean;
  /** The item's CURRENT tags. */
  tags: string[];
  description: string | null;
  taggedAt: string;
}

export interface EnrichmentSinceCounts {
  /** Passes that ended since `since` (tagged + couldNotTag). */
  done: number;
  tagged: number;
  couldNotTag: number;
}

export interface EnrichmentActivity {
  recent: RecentlyTaggedItem[];
  /** Present only when the request carried `since`. */
  sinceCounts?: EnrichmentSinceCounts;
}

export const enrichmentActivityKeys = {
  /** Prefix for invalidating every window at once. Nested under the status
   *  prefix so `knowledgeEnrichmentKeys.all` clears both. */
  all: [...knowledgeEnrichmentKeys.all, 'activity'] as const,
  list: (since: string | null, limit: number) =>
    [...enrichmentActivityKeys.all, { since: since ?? null, limit }] as const,
} as const;

export function useEnrichmentActivity({
  since,
  limit,
  refetchInterval,
}: {
  since: string | null;
  limit: number;
  /** Poll interval in ms while a run is active, `false` for a single read;
   *  a function is re-evaluated after every fetch. */
  refetchInterval: number | false | (() => number | false);
}) {
  return useQuery({
    queryKey: enrichmentActivityKeys.list(since, limit),
    queryFn: () => {
      const qs = new URLSearchParams({ limit: String(limit) });
      if (since) qs.set('since', since);
      return api.get<EnrichmentActivity>(`/api/okf/enrichment/activity?${qs.toString()}`);
    },
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
    refetchInterval,
  });
}
